import React, { useEffect, useState } from 'react';
import { useMode } from '../contexts/ModeContext';
import { useNavigate } from 'react-router-dom';
import { getTranslation } from '../translations';
import { authAPI, progressAPI, setAuthToken } from '../utils/api';
import { BookOpen, BarChart, Settings, LogOut, Play, Flame, Sparkles, Shield, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import EmailVerificationBanner from '../components/EmailVerificationBanner';

const Dashboard = () => {
  const { isKidsMode, language, changeLanguage, setMode } = useMode();
  const navigate = useNavigate();
  const t = (key) => getTranslation(language, key);
  
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [lastPractice, setLastPractice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // Heartbeat ping every 60s (used by admin panel "active minutes" stat)
    const intervalId = setInterval(() => {
      import('../utils/api').then(({ default: apiClient }) => {
        apiClient.post('/user/heartbeat').catch(() => {});
      });
    }, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, statsRes, progressRes] = await Promise.all([
        authAPI.getMe(),
        progressAPI.getStats(),
        progressAPI.getUserProgress()
      ]);
      setUser(userRes.data);
      // Keep the visual theme aligned with the user's saved practice_mode so
      // the Adult/Child theme can never drift from their actual workflow.
      const pm = userRes.data?.practice_mode || 'child';
      setMode(pm === 'adult' ? 'adults' : 'kids');
      setStats(statsRes.data);
      
      // Get last practice
      if (progressRes.data && progressRes.data.length > 0) {
        const sorted = progressRes.data.sort((a, b) => 
          new Date(b.last_practiced) - new Date(a.last_practiced)
        );
        setLastPractice(sorted[0]);
      }
    } catch (error) {
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const continueLastPractice = () => {
    if (lastPractice) {
      // Navigate to practice with state
      navigate('/practice', { 
        state: { 
          continuePractice: true,
          surahNumber: lastPractice.surah_number,
          currentAyah: lastPractice.ayah_to + 1 // Continue from next ayah
        } 
      });
    }
  };

  const handleLogout = () => {
    setAuthToken(null);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        background: isKidsMode
          ? 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)'
          : 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)'
      }}
    >
      {/* Header */}
      <header className="px-6 py-4 bg-white/50 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1
            className={`text-2xl font-bold ${
              isKidsMode ? 'font-kids text-green-500' : 'font-elegant text-emerald-900'
            }`}
          >
            {t('app_name')}
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">{user?.name}</span>
            {user?.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 transition-colors"
                data-testid="dashboard-admin-link"
              >
                <Shield className="w-4 h-4" />
                Admin
              </button>
            )}
            <button
              onClick={handleLogout}
              className={`p-2 rounded-lg hover:bg-gray-100 transition-colors`}
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2
            className={`text-3xl md:text-4xl font-bold mb-2 ${
              isKidsMode ? 'font-kids text-slate-800' : 'font-elegant text-stone-900'
            }`}
            data-testid="welcome-message"
          >
            {t('welcome')}, {user?.name}!
          </h2>
          <p className="text-gray-600">Bugün hangi sureleri çalışacaksınız?</p>
        </div>

        <EmailVerificationBanner user={user} onVerified={fetchData} />

        {/* Continue Practice Card */}
        {lastPractice && (
          <div
            className={`mb-8 p-6 ${
              isKidsMode
                ? 'bg-gradient-to-r from-yellow-100 to-yellow-200 rounded-3xl border-2 border-yellow-300'
                : 'bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl border border-amber-200'
            }`}
            data-testid="continue-practice-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen className={`w-6 h-6 ${isKidsMode ? 'text-yellow-600' : 'text-amber-700'}`} />
                  <h3 className={`font-bold text-lg ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
                    Kaldığın Yerden Devam Et
                  </h3>
                </div>
                <p className="text-sm text-gray-700">
                  Sure {lastPractice.surah_number}, Ayet {lastPractice.ayah_to + 1}'den itibaren
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Son çalışma: {new Date(lastPractice.last_practiced).toLocaleDateString('tr-TR')}
                </p>
              </div>
              <button
                onClick={continueLastPractice}
                className={`px-6 py-3 font-bold text-white transition-all flex items-center gap-2 ${
                  isKidsMode
                    ? 'bg-yellow-500 rounded-full hover:bg-yellow-600 shadow-[0_4px_0_0_rgba(0,0,0,0.2)] hover:shadow-none hover:translate-y-1'
                    : 'bg-amber-600 rounded-md hover:bg-amber-700'
                }`}
                data-testid="continue-practice-button"
              >
                <Play className="w-5 h-5" />
                Devam Et
              </button>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          {[
            {
              label: t('total_attempts'),
              value: stats?.total_attempts || 0,
              color: isKidsMode ? 'bg-green-400' : 'bg-emerald-700'
            },
            {
              label: t('successful_attempts'),
              value: stats?.successful_attempts || 0,
              color: isKidsMode ? 'bg-yellow-400' : 'bg-amber-600'
            },
            {
              label: t('total_errors'),
              value: stats?.total_errors || 0,
              color: isKidsMode ? 'bg-pink-400' : 'bg-rose-600'
            },
            {
              label: t('success_rate'),
              value: `${Math.round(stats?.success_rate || 0)}%`,
              color: isKidsMode ? 'bg-purple-400' : 'bg-teal-600'
            }
          ].map((stat, idx) => (
            <div
              key={idx}
              className={`p-6 text-white transition-transform hover:scale-105 ${
                isKidsMode ? 'rounded-3xl shadow-[0_6px_0_0_rgba(0,0,0,0.2)]' : 'rounded-xl'
              } ${stat.color}`}
              data-testid={`stat-card-${idx}`}
            >
              <div className="text-3xl font-bold mb-1">{stat.value}</div>
              <div className="text-sm opacity-90">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <button
            onClick={() => navigate('/practice')}
            className={`p-8 text-left transition-all hover:scale-105 ${
              isKidsMode
                ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm hover:shadow-md'
            }`}
            data-testid="practice-button"
          >
            <BookOpen
              className={`w-12 h-12 mb-4 ${
                isKidsMode ? 'text-green-400' : 'text-emerald-700'
              }`}
            />
            <h3 className={`text-xl font-bold mb-2 ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
              {t('practice')}
            </h3>
            <p className="text-sm text-gray-600">Kur'an ezber çalışması yap</p>
          </button>

          <button
            onClick={() => navigate('/elifba')}
            className={`p-8 text-left transition-all hover:scale-105 relative overflow-hidden ${
              isKidsMode
                ? 'bg-gradient-to-br from-yellow-100 via-orange-50 to-pink-100 rounded-3xl border-2 border-yellow-300 shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm hover:shadow-md'
            }`}
            data-testid="elifba-button"
          >
            <Sparkles
              className={`w-12 h-12 mb-4 ${
                isKidsMode ? 'text-orange-500' : 'text-amber-700'
              }`}
            />
            <h3 className={`text-xl font-bold mb-2 ${isKidsMode ? 'font-kids text-slate-800' : 'font-elegant text-stone-900'}`}>
              Elif-Ba Öğreniyorum
            </h3>
            <p className="text-sm text-gray-600">Arapça harfleri adım adım öğren</p>
            {isKidsMode && (
              <span className="absolute top-3 right-3 px-2 py-1 text-xs font-bold bg-pink-500 text-white rounded-full">
                YENİ
              </span>
            )}
          </button>

          <button
            onClick={() => navigate('/badges')}
            className={`p-8 text-left transition-all hover:scale-105 ${
              isKidsMode
                ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm hover:shadow-md'
            }`}
            data-testid="badges-button"
          >
            <BarChart
              className={`w-12 h-12 mb-4 ${
                isKidsMode ? 'text-yellow-400' : 'text-amber-600'
              }`}
            />
            <h3 className={`text-xl font-bold mb-2 ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
              Rozetler
            </h3>
            <p className="text-sm text-gray-600">Rozetlerinizi görüntüleyin</p>
          </button>

          <button
            onClick={() => navigate('/social')}
            className={`p-8 text-left transition-all hover:scale-105 ${
              isKidsMode
                ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm hover:shadow-md'
            }`}
            data-testid="social-button"
          >
            <Settings
              className={`w-12 h-12 mb-4 ${
                isKidsMode ? 'text-pink-400' : 'text-teal-600'
              }`}
            />
            <h3 className={`text-xl font-bold mb-2 ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
              Sosyal
            </h3>
            <p className="text-sm text-gray-600">Arkadaşlar ve liderlik</p>
          </button>

          <button
            onClick={() => navigate('/elifba/progress')}
            className={`p-8 text-left transition-all hover:scale-105 ${
              isKidsMode
                ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm hover:shadow-md'
            }`}
            data-testid="voice-progress-button"
          >
            <TrendingUp
              className={`w-12 h-12 mb-4 ${
                isKidsMode ? 'text-emerald-500' : 'text-emerald-700'
              }`}
            />
            <h3 className={`text-xl font-bold mb-2 ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
              İlerleme Raporu
            </h3>
            <p className="text-sm text-gray-600">Sesli tekrar başarı analizi</p>
          </button>
        </div>

        {/* Secondary Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          <button
            onClick={() => navigate('/statistics')}
            className={`p-6 text-left transition-all hover:scale-105 ${
              isKidsMode
                ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm hover:shadow-md'
            }`}
            data-testid="statistics-button"
          >
            <BarChart
              className={`w-8 h-8 mb-3 ${
                isKidsMode ? 'text-purple-400' : 'text-purple-600'
              }`}
            />
            <h3 className={`text-lg font-bold mb-1 ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
              {t('statistics')}
            </h3>
            <p className="text-sm text-gray-600">İlerlemenizi görüntüleyin</p>
          </button>

          <button
            onClick={() => navigate('/settings')}
            className={`p-6 text-left transition-all hover:scale-105 ${
              isKidsMode
                ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
                : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm hover:shadow-md'
            }`}
            data-testid="settings-button"
          >
            <Settings
              className={`w-8 h-8 mb-3 ${
                isKidsMode ? 'text-orange-400' : 'text-orange-600'
              }`}
            />
            <h3 className={`text-lg font-bold mb-1 ${isKidsMode ? 'font-kids' : 'font-elegant'}`}>
              {t('settings')}
            </h3>
            <p className="text-sm text-gray-600">Ayarları düzenleyin</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
